import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';

const SubscribePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual');

  const handleSubscribe = async () => {
    setLoading(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/payments/checkout-session`,
        { 
          origin_url: originUrl, 
          plan_type: selectedPlan,
          metadata: { plan: selectedPlan } 
        },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-4xl p-8 border border-primary/10 shadow-sm">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Subscribe to Rooted
          </h1>
          <p className="text-muted-foreground text-lg">
            Begin your 365-day devotional journey
          </p>
        </div>

        {/* Plan Selection */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Annual Plan */}
          <Card
            className={`p-6 cursor-pointer transition-all ${
              selectedPlan === 'annual'
                ? 'border-2 border-primary shadow-md'
                : 'border border-primary/20 hover:border-primary/40'
            }`}
            onClick={() => setSelectedPlan('annual')}
            data-testid="annual-plan"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-medium mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Annual
                </h3>
                <p className="text-sm text-muted-foreground">Best Value</p>
              </div>
              {selectedPlan === 'annual' && (
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary-foreground" strokeWidth={3} />
                </div>
              )}
            </div>
            <div className="mb-4">
              <span className="text-5xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                $35.99
              </span>
              <span className="text-muted-foreground">/year</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Only $2.50 per month
            </p>
            <div className="text-sm text-green-700 font-medium">
              Save 50% vs monthly
            </div>
          </Card>

          {/* Monthly Plan */}
          <Card
            className={`p-6 cursor-pointer transition-all ${
              selectedPlan === 'monthly'
                ? 'border-2 border-primary shadow-md'
                : 'border border-primary/20 hover:border-primary/40'
            }`}
            onClick={() => setSelectedPlan('monthly')}
            data-testid="monthly-plan"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-medium mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Monthly
                </h3>
                <p className="text-sm text-muted-foreground">Pay as you go</p>
              </div>
              {selectedPlan === 'monthly' && (
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary-foreground" strokeWidth={3} />
                </div>
              )}
            </div>
            <div className="mb-4">
              <span className="text-5xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                $4.99
              </span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Flexible monthly billing
            </p>
            <div className="text-sm text-muted-foreground">
              &nbsp;
            </div>
          </Card>
        </div>

        {/* Features List */}
        <div className="bg-secondary/30 rounded-lg p-8 mb-8">
          <h3 className="text-xl mb-6 font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
            Both plans include:
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary mt-0.5" strokeWidth={2} />
              <p className="text-muted-foreground">365 daily devotionals aligned with the liturgical calendar</p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary mt-0.5" strokeWidth={2} />
              <p className="text-muted-foreground">Personal journal for reflections and responses</p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary mt-0.5" strokeWidth={2} />
              <p className="text-muted-foreground">Audio playback for accessibility</p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary mt-0.5" strokeWidth={2} />
              <p className="text-muted-foreground">Reading streak tracking and daily reminders</p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary mt-0.5" strokeWidth={2} />
              <p className="text-muted-foreground">Bookmark your favorite devotions</p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary mt-0.5" strokeWidth={2} />
              <p className="text-muted-foreground">Access across all your devices</p>
            </div>
          </div>
        </div>

        <Button
          data-testid="checkout-btn"
          className="w-full rounded-full py-6 text-lg"
          onClick={handleSubscribe}
          disabled={loading}
        >
          {loading ? 'Redirecting...' : `Subscribe ${selectedPlan === 'annual' ? 'Annually' : 'Monthly'}`}
        </Button>

        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-muted-foreground">
            Want to see what you're getting?{' '}
            <button
              onClick={() => navigate('/preview')}
              className="text-primary hover:underline font-medium"
              data-testid="preview-link"
            >
              View sample devotional
            </button>
          </p>
          <p className="text-sm text-muted-foreground">
            Already subscribed?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-primary hover:underline"
              data-testid="login-link"
            >
              Sign in
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default SubscribePage;